<template>
    <div>
        <template v-if="!drawerOnly">
            <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <div ref="libraryFocusTarget" tabindex="-1" class="text-lg font-extrabold tracking-tight">Бібліотека</div>
                    <div
                        v-if="(isOwner && ownerLibraryLoadState === 'loaded') || (!isOwner && publicLibraryLoaded)"
                        class="hidden text-sm text-foxy-text-secondary md:block"
                    >
                        Книг у бібліотеці: {{ libraryCounts.total }}
                    </div>
                </div>
                <div
                    v-if="isOwner"
                    class="library-view-switcher hidden items-center rounded-xl border border-foxy-border bg-foxy-surface/80 p-1 md:inline-flex"
                    role="group"
                    aria-label="Вигляд бібліотеки"
                >
                    <button
                        type="button"
                        class="library-view-switcher-button"
                        :class="{ 'library-view-switcher-button--active': ownerViewMode === 'cards' }"
                        :aria-pressed="ownerViewMode === 'cards' ? 'true' : 'false'"
                        @click="setOwnerViewMode('cards')"
                    >
                        Картки
                    </button>
                    <button
                        type="button"
                        class="library-view-switcher-button"
                        :class="{ 'library-view-switcher-button--active': ownerViewMode === 'shelves' }"
                        :aria-pressed="ownerViewMode === 'shelves' ? 'true' : 'false'"
                        @click="setOwnerViewMode('shelves')"
                    >
                        Полички
                    </button>
                </div>
            </div>
            <template v-if="isOwner">
                <nav class="library-shelves scrollbar-hide mt-3 hidden gap-2 overflow-x-auto pb-1 md:flex" aria-label="Полиці бібліотеки">
                    <button
                        v-for="shelf in shelfNavigation"
                        :key="shelf.status || 'all'"
                        type="button"
                        class="library-shelf-button"
                        :class="{ 'library-shelf-button--active': filters.status === shelf.status }"
                        :aria-current="filters.status === shelf.status ? 'page' : null"
                        :disabled="loading"
                        @click="selectShelf(shelf.status)"
                    >
                        <span>{{ shelf.label }}</span>
                        <span class="library-shelf-count">{{ libraryCounts[shelf.countKey] }}</span>
                    </button>
                </nav>

                <div class="library-owner-toolbar acc-glass mt-2 hidden rounded-2xl border border-white/10 p-3 md:block">
                    <div class="grid grid-cols-1 gap-2.5 md:grid-cols-[minmax(0,1fr)_auto_auto_auto]">
                        <div class="relative">
                            <input
                                v-model.trim="filters.q"
                                type="text"
                                class="acc-modal-input pr-10"
                                placeholder="Назва або автор"
                                @keyup.enter="applyFilters"
                            />
                            <button
                                v-if="filters.q || appliedFilters.q"
                                type="button"
                                class="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-xl text-lg text-foxy-text-secondary hover:text-foxy-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-foxy-action"
                                aria-label="Очистити пошук"
                                @click="clearSearch"
                            >
                                <span aria-hidden="true">×</span>
                            </button>
                        </div>
                        <button
                            type="button"
                            class="library-filter-trigger acc-btn relative shrink-0"
                            :class="{ 'library-filter-trigger--active': hasActiveLibraryFilters }"
                            :aria-expanded="filtersPanelOpen ? 'true' : 'false'"
                            aria-controls="owner-library-filters"
                            @click="toggleLibraryFilters"
                        >
                            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M7 12h10M10 18h4" />
                            </svg>
                            <span>Фільтри</span>
                            <span v-if="activeLibraryFilterCount" class="library-filter-count" aria-label="Активних фільтрів">
                                {{ activeLibraryFilterCount }}
                            </span>
                        </button>
                        <div class="library-sort-control-group flex min-w-0 items-stretch gap-1">
                            <select
                                v-model="ownerSortBy"
                                class="library-sort-criterion acc-modal-input min-w-0"
                                aria-label="Критерій сортування бібліотеки"
                                :disabled="loading"
                                @change="changeSortCriterion"
                            >
                                <option value="activity">За активністю</option>
                                <option value="added_at">За датою додавання</option>
                                <option value="title">За назвою</option>
                            </select>
                            <button
                                type="button"
                                class="library-sort-direction acc-btn shrink-0"
                                :disabled="loading"
                                :aria-label="sortDirectionLabel"
                                :title="sortDirectionLabel"
                                @click="toggleSortDirection"
                            >
                                <span class="library-sort-direction-icon" aria-hidden="true">
                                    {{ ownerSortDirection === 'desc' ? '↓' : '↑' }}
                                </span>
                            </button>
                        </div>
                        <button class="acc-btn" type="button" :disabled="loading" @click="applyFilters">
                            {{ loading ? 'Завантаження...' : 'Знайти' }}
                        </button>
                    </div>
                    <section
                        v-if="filtersPanelOpen"
                        id="owner-library-filters"
                        class="library-filter-panel"
                        role="dialog"
                        aria-labelledby="owner-library-filters-title"
                        @keydown.esc="cancelLibraryFilters"
                    >
                        <div class="flex items-start justify-between gap-4">
                            <div>
                                <h2
                                    id="owner-library-filters-title"
                                    ref="libraryFilterPanelTitle"
                                    tabindex="-1"
                                    class="text-sm font-extrabold text-foxy-text"
                                >
                                    Фільтри
                                </h2>
                                <p class="mt-0.5 text-xs text-foxy-text-secondary">Зміни застосуються лише після підтвердження.</p>
                            </div>
                            <button type="button" class="library-filter-close" aria-label="Закрити фільтри" @click="cancelLibraryFilters">×</button>
                        </div>

                        <div class="mt-3 grid gap-3 lg:grid-cols-3">
                            <fieldset class="library-filter-fieldset library-filter-fieldset--rating">
                                <legend>Моя оцінка</legend>
                                <div class="library-rating-range mt-1.5" role="group" aria-label="Діапазон особистої оцінки">
                                    <output class="library-rating-range-value" aria-live="polite">
                                        {{ ratingDraftMinimumValue }} — {{ ratingDraftMaximumValue }}
                                    </output>
                                    <div class="library-rating-range-control" :style="ratingDraftRangeStyle">
                                        <span class="library-rating-range-track" aria-hidden="true"></span>
                                        <input
                                            class="library-rating-range-input library-rating-range-input--minimum"
                                            :class="{ 'library-rating-range-input--front': ratingDraftMinimumValue >= ratingDraftMaximumValue - 1 }"
                                            type="range"
                                            min="1"
                                            max="10"
                                            step="1"
                                            :value="ratingDraftMinimumValue"
                                            :aria-valuetext="`${ratingDraftMinimumValue} з 10`"
                                            aria-label="Мінімальна оцінка"
                                            @input="updateRatingDraftMinimum($event.target.value)"
                                        />
                                        <input
                                            class="library-rating-range-input library-rating-range-input--maximum"
                                            type="range"
                                            min="1"
                                            max="10"
                                            step="1"
                                            :value="ratingDraftMaximumValue"
                                            :aria-valuetext="`${ratingDraftMaximumValue} з 10`"
                                            aria-label="Максимальна оцінка"
                                            @input="updateRatingDraftMaximum($event.target.value)"
                                        />
                                    </div>
                                    <div class="library-rating-range-scale" aria-hidden="true"><span>1</span><span>10</span></div>
                                </div>
                            </fieldset>

                            <fieldset class="library-filter-fieldset">
                                <legend>Період читання</legend>
                                <div class="mt-2 grid grid-cols-2 gap-2">
                                    <label class="library-filter-label">
                                        Від року
                                        <input
                                            v-model="filterDraft.year_from"
                                            type="number"
                                            min="1000"
                                            max="9999"
                                            inputmode="numeric"
                                            class="acc-modal-input"
                                            placeholder="Напр. 2023"
                                            @input="clearLibraryFilterError"
                                        />
                                    </label>
                                    <label class="library-filter-label">
                                        До року
                                        <input
                                            v-model="filterDraft.year_to"
                                            type="number"
                                            min="1000"
                                            max="9999"
                                            inputmode="numeric"
                                            class="acc-modal-input"
                                            placeholder="Напр. 2025"
                                            @input="clearLibraryFilterError"
                                        />
                                    </label>
                                </div>
                                <label class="library-filter-toggle mt-2.5">
                                    <input v-model="filterDraft.without_year" type="checkbox" @change="clearLibraryFilterError" />
                                    <span>Без вказаного року</span>
                                </label>
                            </fieldset>

                            <fieldset class="library-filter-fieldset">
                                <legend>Додатково</legend>
                                <label class="library-filter-toggle mt-2.5">
                                    <input v-model="filterDraft.paused_only" type="checkbox" @change="clearLibraryFilterError" />
                                    <span>Відкладене читання</span>
                                </label>
                                <p class="mt-2 text-xs leading-relaxed text-foxy-text-secondary">
                                    Показує книги з поточним призупиненим прочитанням.
                                </p>
                            </fieldset>
                        </div>

                        <p v-if="filterDraftError" class="mt-3 text-xs font-semibold text-foxy-error" role="alert">{{ filterDraftError }}</p>
                        <div class="mt-4 flex items-center justify-end gap-2 border-t border-foxy-border pt-3">
                            <button type="button" class="acc-btn" @click="resetLibraryFilterDraft">Скинути</button>
                            <button type="button" class="acc-btn library-filter-apply" :disabled="loading" @click="applyLibraryFilters">
                                {{ loading ? 'Завантаження...' : 'Застосувати' }}
                            </button>
                        </div>
                    </section>

                    <div v-if="hasActiveLibraryFilters" class="library-filter-chips" aria-label="Активні фільтри">
                        <button
                            v-for="chip in activeLibraryFilterChips"
                            :key="chip.key"
                            type="button"
                            class="library-filter-chip"
                            :aria-label="`Прибрати фільтр: ${chip.label}`"
                            :disabled="loading"
                            @click="removeLibraryFilter(chip.key)"
                        >
                            <span>{{ chip.label }}</span>
                            <span aria-hidden="true">×</span>
                        </button>
                        <button
                            v-if="activeLibraryFilterCount >= 3"
                            type="button"
                            class="library-filter-clear-all"
                            :disabled="loading"
                            @click="clearAllLibraryFilters"
                        >
                            Скинути все
                        </button>
                    </div>
                    <div v-if="ownerLibraryLoadState === 'loaded' && hasActiveResultConstraint" class="mt-2 text-xs text-white/60">
                        Знайдено: {{ pagination.total }}
                    </div>
                </div>
            </template>

            <template v-else>
                <nav class="library-shelves scrollbar-hide mt-3 flex gap-2 overflow-x-auto pb-1" aria-label="Полиці публічної бібліотеки">
                    <button
                        v-for="shelf in shelfNavigation"
                        :key="shelf.status || 'all'"
                        type="button"
                        class="library-shelf-button"
                        :class="{ 'library-shelf-button--active': filters.status === shelf.status }"
                        :aria-current="filters.status === shelf.status ? 'page' : null"
                        :disabled="loading"
                        @click="selectShelf(shelf.status)"
                    >
                        <span>{{ shelf.label }}</span>
                        <span v-if="publicLibraryLoaded" class="library-shelf-count">{{ libraryCounts[shelf.countKey] }}</span>
                    </button>
                </nav>

                <div class="library-public-toolbar mt-2">
                    <div class="grid grid-cols-1 gap-2.5 md:grid-cols-[minmax(0,1fr)_auto_auto_auto]">
                        <div class="relative">
                            <input
                                v-model.trim="filters.q"
                                type="text"
                                class="acc-modal-input pr-10"
                                placeholder="Назва або автор"
                                @keyup.enter="applyFilters"
                            />
                            <button
                                v-if="filters.q || appliedFilters.q"
                                type="button"
                                class="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-xl text-lg text-foxy-text-secondary hover:text-foxy-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-foxy-action"
                                aria-label="Очистити пошук"
                                @click="clearSearch"
                            >
                                <span aria-hidden="true">×</span>
                            </button>
                        </div>
                        <select
                            v-model="publicSortBy"
                            class="library-sort-criterion acc-modal-input min-w-0"
                            aria-label="Критерій сортування публічної бібліотеки"
                            :disabled="loading"
                            @change="changeSortCriterion"
                        >
                            <option value="activity">За активністю</option>
                            <option value="added_at">За датою додавання</option>
                            <option value="title">За назвою</option>
                        </select>
                        <button
                            type="button"
                            class="library-sort-direction acc-btn shrink-0"
                            :disabled="loading"
                            :aria-label="publicSortDirectionLabel"
                            :title="publicSortDirectionLabel"
                            @click="togglePublicSortDirection"
                        >
                            <span class="library-sort-direction-icon" aria-hidden="true">{{ publicSortDirection === 'desc' ? '↓' : '↑' }}</span>
                        </button>
                        <button class="acc-btn" type="button" :disabled="loading" @click="applyFilters">
                            {{ loading ? 'Завантаження...' : 'Знайти' }}
                        </button>
                    </div>
                </div>
            </template>

            <div v-if="isOwner" class="acc-glass mt-4 rounded-2xl border border-white/10 p-4 md:hidden">
                <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
                    <input
                        v-model.trim="filters.q"
                        type="text"
                        class="acc-modal-input xl:col-span-2"
                        :placeholder="isOwner ? 'Назва або автор' : 'Пошук за назвою книги...'"
                        @keyup.enter="applyFilters"
                    />
                    <select v-model="filters.status" class="acc-modal-input" @change="applyFilters">
                        <option value="">Усі статуси</option>
                        <option value="want_to_read">Хочу прочитати</option>
                        <option value="reading">Читаю</option>
                        <option value="read">Прочитано</option>
                        <option value="abandoned">Не дочитано</option>
                    </select>
                    <label v-if="isOwner" class="flex flex-col gap-1 text-xs text-white/65">
                        Моя оцінка
                        <select v-model="filters.rating" class="acc-modal-input" @change="applyFilters">
                            <option value="">Усі оцінки</option>
                            <option v-for="rating in 10" :key="rating" :value="String(rating)">{{ rating }}/10</option>
                        </select>
                    </label>
                </div>
                <div class="mt-3 flex items-center gap-2">
                    <button class="acc-btn" type="button" :disabled="loading" @click="applyFilters">
                        {{ loading ? 'Завантаження...' : 'Застосувати' }}
                    </button>
                    <button class="acc-btn" type="button" :disabled="loading" @click="resetFilters">Скинути</button>
                    <div v-if="!isOwner || ownerLibraryLoadState === 'loaded'" class="text-xs text-white/60">Знайдено: {{ pagination.total }}</div>
                </div>
            </div>

            <section
                v-if="isOwner"
                v-show="ownerViewMode === 'shelves'"
                ref="shelfLayout"
                class="library-shelves-view mt-5 hidden md:block"
                aria-label="Книги на поличках"
            >
                <div v-if="ownerLibraryLoadState === 'error'" class="library-shelves-message" role="alert">
                    <p>{{ ownerLibraryLoadError || 'Не вдалося завантажити бібліотеку.' }}</p>
                    <button type="button" class="acc-btn mt-3" @click="retryOwnerLibrary">Спробувати ще раз</button>
                </div>
                <div v-else-if="ownerLibraryLoadState !== 'loaded'" class="library-shelves-message" role="status">Завантаження бібліотеки…</div>
                <div v-else-if="!books.length" class="library-shelves-message">
                    {{ libraryCounts.total > 0 ? 'За вашим пошуком і фільтрами нічого не знайдено.' : 'У бібліотеці поки немає книг.' }}
                </div>
                <div v-else class="library-shelf-rows" :style="{ '--library-shelf-columns': shelfColumns }">
                    <ul v-for="(row, rowIndex) in shelfRows" :key="rowIndex" class="library-shelf-row" aria-label="Ряд книг">
                        <li v-for="item in row" :key="item.id" class="library-shelf-item">
                            <div class="library-shelf-cover-stage">
                                <a
                                    v-if="item.book && item.book.slug"
                                    :href="`/books/${item.book.slug}`"
                                    class="library-shelf-cover book-cover-frame"
                                    :aria-label="`Відкрити книгу «${item.book.title}»`"
                                    @mouseenter="openCardPreview(item, $event)"
                                    @mouseleave="schedulePreviewClose"
                                    @focus="openCardPreview(item, $event, true)"
                                    @blur="schedulePreviewClose"
                                >
                                    <img :src="resolveBookCover(item.book.cover)" class="book-cover-media" alt="" @error="onCoverError" />
                                    <book-status-bookmark :status="contextFor(item).shelfStatus" />
                                </a>
                            </div>
                            <div class="library-shelf-caption">
                                <a
                                    v-if="item.book && item.book.slug"
                                    :href="`/books/${item.book.slug}`"
                                    class="library-shelf-title"
                                    :title="item.book.title"
                                >
                                    {{ item.book.title }}
                                </a>
                                <span v-else class="library-shelf-title">{{ item.book && item.book.title ? item.book.title : 'Книга' }}</span>
                                <span v-if="item.book && item.book.author" class="library-shelf-author" :title="item.book.author">
                                    <template v-if="item.book.authors && item.book.authors.length">
                                        <span v-for="(author, index) in item.book.authors" :key="`${item.id}-shelf-author-${index}`">
                                            <span v-if="index">,</span>
                                            <a
                                                v-if="author.url"
                                                :href="author.url"
                                                class="hover:text-foxy-action hover:underline focus-visible:text-foxy-action focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-foxy-action"
                                                >{{ author.name }}</a
                                            >
                                            <span v-else>{{ author.name }}</span>
                                        </span>
                                    </template>
                                    <template v-else>{{ item.book.author }}</template>
                                </span>
                                <div class="library-shelf-actions">
                                    <a :href="managementLink(item)" class="library-shelf-manage" @click.prevent="openAction(item, $event)"
                                        >Керувати</a
                                    >
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </section>

            <div
                class="library-book-grid mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2"
                :class="{ 'md:hidden': isOwner && ownerViewMode === 'shelves' }"
            >
                <div
                    v-if="isOwner && ownerLibraryLoadState === 'error'"
                    class="library-grid-message acc-glass rounded-2xl border border-foxy-error/40 p-4 text-sm text-foxy-error"
                    role="alert"
                >
                    <p>{{ ownerLibraryLoadError || 'Не вдалося завантажити бібліотеку.' }}</p>
                    <button type="button" class="acc-btn mt-3" @click="retryOwnerLibrary">Спробувати ще раз</button>
                </div>
                <div
                    v-else-if="isOwner && ownerLibraryLoadState !== 'loaded'"
                    class="library-grid-message acc-glass rounded-2xl border border-white/10 p-4 text-sm text-white/65"
                    role="status"
                >
                    Завантаження бібліотеки…
                </div>
                <template v-else>
                    <article
                        v-for="item in books"
                        :key="item.id || (item.book && item.book.slug)"
                        class="acc-glass flex flex-col rounded-2xl border border-white/10"
                        :class="{ 'library-owner-card': isOwner }"
                        @mouseenter="openCardPreview(item, $event)"
                        @mouseleave="schedulePreviewClose"
                    >
                        <div v-if="isOwner" class="hidden min-h-6 items-center justify-end px-3 py-0.5 text-xs text-foxy-text-secondary md:flex">
                            <span v-if="membershipDate(item)" :title="membershipDate(item).accessible" :aria-label="membershipDate(item).accessible">
                                {{ membershipDate(item).visual }}
                            </span>
                        </div>
                        <a
                            v-if="item.book && item.book.slug"
                            :href="`/books/${item.book.slug}`"
                            class="book-cover-frame block aspect-[3/4] h-auto w-full rounded-t-2xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/45"
                            :aria-label="`Відкрити книгу «${item.book.title}»`"
                        >
                            <img :src="resolveBookCover(item.book.cover)" class="book-cover-media" alt="" @error="onCoverError" />
                        </a>
                        <div v-else class="book-cover-frame aspect-[3/4] h-auto w-full rounded border border-white/10">
                            <img :src="resolveBookCover(item.book && item.book.cover)" class="book-cover-media" alt="" @error="onCoverError" />
                        </div>
                        <div class="flex flex-1 flex-col p-2.5">
                            <template v-if="isOwner">
                                <div class="book-card-status-row flex flex-wrap items-center gap-1.5">
                                    <book-user-state-badge class="book-card-status-badge" :status="contextFor(item).shelfStatus" />
                                    <span
                                        v-if="personalRating(item) !== null"
                                        class="book-card-rating-chip rounded-full border border-foxy-border bg-foxy-elevated px-2 py-1 text-xs font-extrabold text-foxy-text dark:border-foxy-border/60 dark:bg-foxy-elevated/70 dark:font-semibold dark:text-foxy-text-secondary"
                                        :aria-label="`Ваша оцінка: ${personalRating(item)} з 10`"
                                    >
                                        {{ personalRating(item) }}/10
                                    </span>
                                </div>
                                <a
                                    v-if="item.book && item.book.slug"
                                    :href="`/books/${item.book.slug}`"
                                    class="mt-1.5 line-clamp-2 block text-sm font-bold text-foxy-text hover:text-foxy-action"
                                    :title="item.book.title"
                                >
                                    {{ item.book.title }}
                                </a>
                                <div
                                    v-if="item.book && item.book.author"
                                    class="mt-1 truncate text-xs text-foxy-text-secondary"
                                    :title="item.book.author"
                                >
                                    <template v-if="item.book.authors && item.book.authors.length">
                                        <span v-for="(author, index) in item.book.authors" :key="`${item.id}-author-${index}`">
                                            <span v-if="index">,</span>
                                            <a
                                                v-if="author.url"
                                                :href="author.url"
                                                class="hover:text-foxy-action hover:underline focus-visible:text-foxy-action focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-foxy-action"
                                                >{{ author.name }}</a
                                            >
                                            <span v-else>{{ author.name }}</span>
                                        </span>
                                    </template>
                                    <template v-else>{{ item.book.author }}</template>
                                </div>
                                <div v-if="cardMetadata(item).length" class="mt-2 truncate text-xs text-foxy-text-secondary md:hidden">
                                    <span v-for="fact in cardMetadata(item)" :key="fact.key" :title="fact.title || fact.label">{{ fact.label }}</span>
                                </div>
                                <div v-if="completionDateLabel(item)" class="mt-2 hidden text-xs text-foxy-text-secondary md:block">
                                    {{ completionDateLabel(item) }}
                                </div>
                                <div class="relative mt-auto flex flex-wrap items-center gap-2 pt-2.5">
                                    <a
                                        :ref="`bookAction-${item.id}`"
                                        :href="managementLink(item)"
                                        class="acc-btn min-h-10 !px-3 !py-2 text-xs"
                                        @click.prevent="openAction(item, $event)"
                                    >
                                        Керувати
                                    </a>
                                    <button
                                        v-if="previewAvailable"
                                        type="button"
                                        class="book-preview-trigger acc-btn hidden text-xs md:inline-flex"
                                        aria-label="Швидкий перегляд"
                                        :aria-controls="previewId"
                                        :aria-expanded="isPreviewOpen(item) ? 'true' : 'false'"
                                        @focus="openCardPreview(item, $event, true)"
                                        @blur="schedulePreviewClose"
                                        @click="openCardPreview(item, $event, true)"
                                        @keydown="handlePreviewTriggerKeydown($event, item)"
                                    >
                                        <span>Швидкий перегляд</span>
                                    </button>
                                </div>
                            </template>
                            <template v-else>
                                <div class="book-card-status-row flex flex-wrap items-center gap-1.5">
                                    <book-user-state-badge class="book-card-status-badge" :status="item.status" />
                                    <span
                                        v-if="personalRating(item) !== null"
                                        class="book-card-rating-chip rounded-full border border-foxy-border bg-foxy-elevated px-2 py-1 text-xs font-extrabold text-foxy-text dark:border-foxy-border/60 dark:bg-foxy-elevated/70 dark:font-semibold dark:text-foxy-text-secondary"
                                        :aria-label="`Оцінка власника профілю: ${personalRating(item)} з 10`"
                                    >
                                        {{ personalRating(item) }}/10
                                    </span>
                                </div>
                                <a
                                    v-if="item.book && item.book.slug"
                                    :href="`/books/${item.book.slug}`"
                                    class="mt-1.5 line-clamp-2 block text-sm font-bold text-foxy-text hover:text-foxy-action focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foxy-action"
                                    :title="item.book.title"
                                >
                                    {{ item.book.title }}
                                </a>
                                <div v-else class="mt-1.5 line-clamp-2 text-sm font-bold text-foxy-text">
                                    {{ item.book ? item.book.title : 'Книга' }}
                                </div>
                                <div
                                    v-if="item.book && item.book.author"
                                    class="mt-1 truncate text-xs text-foxy-text-secondary"
                                    :title="item.book.author"
                                >
                                    <template v-if="item.book.authors && item.book.authors.length">
                                        <span
                                            v-for="(author, index) in item.book.authors"
                                            :key="`${item.book.slug || 'book'}-public-author-${index}`"
                                        >
                                            <span v-if="index">,</span>
                                            <a
                                                v-if="author.url"
                                                :href="author.url"
                                                class="hover:text-foxy-action hover:underline focus-visible:text-foxy-action focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-foxy-action"
                                                >{{ author.name }}</a
                                            >
                                            <span v-else>{{ author.name }}</span>
                                        </span>
                                    </template>
                                    <template v-else>{{ item.book.author }}</template>
                                </div>
                                <div class="relative">
                                    <button
                                        v-if="previewAvailable"
                                        type="button"
                                        class="book-preview-trigger acc-btn hidden text-xs md:inline-flex"
                                        aria-label="Швидкий перегляд"
                                        :aria-controls="previewId"
                                        :aria-expanded="isPreviewOpen(item) ? 'true' : 'false'"
                                        :disabled="Boolean(viewer) && !viewerContexts[item.book.id]"
                                        @focus="openCardPreview(item, $event, true)"
                                        @blur="schedulePreviewClose"
                                        @click="openCardPreview(item, $event, true)"
                                        @keydown="handlePreviewTriggerKeydown($event, item)"
                                    >
                                        <span>Швидкий перегляд</span>
                                    </button>
                                </div>
                            </template>
                        </div>
                    </article>
                    <div v-if="!books.length" class="library-grid-message acc-glass rounded-2xl border border-white/10 p-4 text-sm text-white/65">
                        {{
                            isOwner && libraryCounts.total > 0 ? 'За вашим пошуком і фільтрами нічого не знайдено.' : 'У бібліотеці поки немає книг.'
                        }}
                    </div>
                </template>
            </div>

            <div
                v-if="(!isOwner || ownerLibraryLoadState === 'loaded') && pagination.last_page > 1"
                class="mt-4 flex items-center gap-3"
                aria-label="Сторінки бібліотеки"
            >
                <button
                    class="acc-btn"
                    type="button"
                    :disabled="loading || pagination.current_page <= 1"
                    @click="loadBooks(pagination.current_page - 1)"
                >
                    Назад
                </button>
                <span class="text-sm">{{ pagination.current_page }} / {{ pagination.last_page }}</span>
                <button
                    class="acc-btn"
                    type="button"
                    :disabled="loading || pagination.current_page >= pagination.last_page"
                    @click="loadBooks(pagination.current_page + 1)"
                >
                    Далі
                </button>
            </div>

            <book-quick-preview-host
                v-if="previewAvailable"
                ref="quickPreview"
                :items="previewItems"
                :refresh-items="isOwner ? refreshBooks : refreshViewerContexts"
                :surface-context="isOwner ? 'OWNER_LIBRARY' : 'PUBLIC_FOREIGN_LIBRARY'"
                :authenticated="previewAvailable"
                @manage="handlePreviewManage"
                @remove="handlePreviewRemove"
                @opened="previewOpenItemId = $event"
                @closed="previewOpenItemId = null"
            />
        </template>

        <profile-library
            v-if="!drawerOnly && !isOwner && viewer"
            ref="viewerManagement"
            :profile="viewer"
            :is-owner="true"
            :drawer-only="true"
            :refresh-external-context="refreshViewerContexts"
        />

        <modal-shell
            ref="profileEditShell"
            :show="Boolean(actionModal) && isOwner"
            mode="drawer"
            title-id="profile-library-edit-title"
            description-id="profile-library-edit-description"
            modal-class="library-history-drawer"
            surface-class="library-history-surface"
            overlay-class="library-history-overlay bg-foxy-cocoa/55 backdrop-blur-sm"
            initial-focus-selector="[data-profile-edit-initial]"
            :focus-dialog-on-open="!actionModalOpenedByKeyboard"
            @escape="requestCloseActionModal"
        >
            <div v-if="actionModal" class="flex h-full min-h-0 flex-col">
                <header class="flex shrink-0 items-center gap-3 border-b border-foxy-border px-4 py-3 sm:px-6">
                    <img
                        :src="resolveBookCover(actionModal.book && actionModal.book.cover)"
                        alt=""
                        class="hidden h-16 w-12 shrink-0 rounded-md border border-foxy-border object-cover sm:block"
                        @error="onCoverError"
                    />
                    <div class="min-w-0 flex-1">
                        <div class="flex flex-wrap items-center gap-2">
                            <book-user-state-badge v-if="editForm.status !== null" :status="editForm.status" />
                            <span class="text-xs text-foxy-text-secondary">Прочитань: {{ editForm.times_read }}</span>
                        </div>
                        <h2 id="profile-library-edit-title" class="mt-1 truncate text-base font-extrabold">{{ actionModal.title }}</h2>
                        <p id="profile-library-edit-description" class="sr-only">{{ actionModal.subtitle }}</p>
                        <a v-if="actionModal.bookUrl" :href="actionModal.bookUrl" class="text-xs font-semibold text-foxy-action hover:underline"
                            >Відкрити книгу</a
                        >
                    </div>
                    <button
                        type="button"
                        class="acc-btn min-h-11 shrink-0 !px-3"
                        aria-label="Закрити керування історією"
                        @click="requestCloseActionModal"
                    >
                        <span class="sm:hidden">Назад</span><span class="hidden sm:inline">Закрити</span>
                    </button>
                </header>

                <library-reading-history-manager
                    ref="historyManager"
                    :form="editForm"
                    :history-state="historyState"
                    :language-label="languageLabel"
                    :language-preferences-state="languagePreferencesState"
                    :reading-mutation-state="readingMutationState"
                    :user-libraries="userLibraries"
                    :memberships-loading="membershipsLoading"
                    :membership-load-failed="membershipLoadFailed"
                    :membership-conflict="membershipConflict"
                    :membership-recovery-error="membershipRecoveryError"
                    :membership-recovery-loading="membershipRecoveryLoading"
                    :save-disabled="membershipConflict !== null || editForm.status === null"
                    :saving="drawerBusy"
                    :book-url="actionModal.bookUrl"
                    :initial-section="actionModal.initialSection"
                    :initial-reading-instance-id="actionModal.initialReadingInstanceId"
                    :open-initial-reading-editor="actionModal.openInitialReadingEditor"
                    class="break-words sm:flex-row"
                    @retry-history="retryHistory"
                    @retry-memberships="retryMemberships"
                    @recover-memberships="recoverMemberships"
                    @retry-language-preferences="loadReadingLanguagePreferences(true)"
                    @clear-reading-errors="clearReadingErrors"
                    @save-reading-instance="saveReadingInstance"
                    @create-historical-reading="createHistoricalReading"
                    @update-form="handleEditFormUpdate"
                    @delete-reading="manageReading"
                    @remove-library="removeFromLibrary"
                    @close="requestCloseActionModal"
                    @save="saveEdit"
                />
            </div>
        </modal-shell>

        <decision-modal ref="ratingClearDecision" />
    </div>
</template>

<script lang="js">
import axios from 'axios';
import ISO6391 from 'iso-639-1';
import bookStatusCache from '../../utils/bookStatusCache';
import { normalizeBookCardContext } from '../../utils/bookCardContext';
import { bookManagementLink } from '../../utils/bookManagementLink';
import { bookStatusPresentation } from '../../utils/bookStatusPresentation';
import { requestLibraryRemoval } from '../../utils/libraryRemoval';
import { fetchReadingRecovery, requestReadingRecovery } from '../../utils/readingRecovery';
import BookQuickPreviewHost from '../../components/books/BookQuickPreviewHost.vue';
import { canUseBookQuickPreview } from '../../utils/bookQuickPreviewCapability';
import BookStatusBookmark from '../../components/books/BookStatusBookmark.vue';
import BookUserStateBadge from '../../components/books/BookUserStateBadge.vue';
import ModalShell from '../../components/ModalShell.vue';
import LibraryReadingHistoryManager from '../components/LibraryReadingHistoryManager.vue';
import readingLanguagePreferences from '../services/readingLanguagePreferences';

const emptyReadingErrors = () => ({
    form: '',
    language: { membership: '', primary: '' },
    started: { group: '', precision: '', year: '', month: '', day: '' },
    finished: { group: '', precision: '', year: '', month: '', day: '' },
    dateRange: '',
    format: { group: '', byCode: {} },
    rating: '',
});

const emptyLibraryFilters = () => ({
    rating_min: null,
    rating_max: null,
    year_from: null,
    year_to: null,
    without_year: false,
    paused_only: false,
});

const committedMutationRefreshError = 'Зміни збережено, але не вдалося оновити бібліотеку.';

export default {
    name: 'ProfileLibrary',
    components: { BookQuickPreviewHost, BookStatusBookmark, BookUserStateBadge, LibraryReadingHistoryManager, ModalShell },
    props: {
        profile: { type: Object, default: null },
        dashboard: { type: Object, default: null },
        isOwner: { type: Boolean, default: false },
        viewer: { type: Object, default: null },
        drawerOnly: { type: Boolean, default: false },
        refreshExternalContext: { type: Function, default: null },
    },
    data() {
        return {
            books: [],
            viewerContexts: {},
            previewBookFacts: {},
            viewerContextState: 'idle',
            viewerContextGeneration: 0,
            booksLoadGeneration: 0,
            requestedOwnerPage: 1,
            pagination: { current_page: 1, last_page: 1, total: 0 },
            libraryCounts: { total: 0, want_to_read: 0, reading: 0, read: 0, abandoned: 0 },
            loading: false,
            ownerLibraryLoadState: this.isOwner ? 'idle' : 'loaded',
            ownerLibraryLoadError: '',
            ownerSortBy: 'activity',
            ownerSortDirection: 'desc',
            publicSortBy: 'activity',
            publicSortDirection: 'desc',
            publicLibraryLoaded: false,
            ownerViewMode: 'cards',
            shelfColumns: 6,
            shelfResizeObserver: null,
            filtersPanelOpen: false,
            filterDraft: emptyLibraryFilters(),
            appliedLibraryFilters: emptyLibraryFilters(),
            filterDraftError: '',
            appliedFilters: { q: '', status: '', rating: '' },
            filters: {
                q: '',
                status: '',
                rating: '',
            },
            actionModal: null,
            actionModalOpenedByKeyboard: false,
            ratingClearConfirmationPending: false,
            saving: false,
            userLibraries: [],
            libraryCatalogLoaded: false,
            libraryCatalogPending: null,
            membershipsLoaded: false,
            membershipsLoading: false,
            membershipLoadFailed: false,
            membershipLoadGeneration: 0,
            membershipSnapshot: null,
            membershipConflict: null,
            membershipRecoveryError: '',
            membershipRecoveryLoading: false,
            historyCache: {},
            historyPending: {},
            historyPendingAuthority: {},
            historyAuthority: {},
            historyAuthoritySequence: 0,
            historyLoadGeneration: 0,
            historyState: {
                status: 'idle',
                data: null,
                error: '',
            },
            languagePreferencesState: {
                status: 'idle',
                data: null,
                error: '',
            },
            readingMutationState: {
                savingReadingId: null,
                errors: emptyReadingErrors(),
                message: '',
                messageType: '',
            },
            editForm: {
                status: 'want_to_read',
                times_read: 0,
                sessions: [],
                reading_instances: [],
                library_ids: [],
                lock_version: null,
            },
            statusSelectionDirty: false,
            previewId: 'book-quick-preview-owner-library',
            previewOpenItemId: null,
        };
    },
    computed: {
        previewAvailable() {
            return canUseBookQuickPreview(Boolean(this.viewer));
        },
        previewItems() {
            if (!this.previewAvailable) return [];
            return this.isOwner ? this.books : this.books.map((item) => this.publicPreviewItem(item)).filter(Boolean);
        },
        ratingDraftMinimumValue() {
            const value = Number(this.filterDraft.rating_min);
            return Number.isInteger(value) && value >= 1 && value <= 10 ? value : 1;
        },
        ratingDraftMaximumValue() {
            const value = Number(this.filterDraft.rating_max);
            return Number.isInteger(value) && value >= 1 && value <= 10 ? value : 10;
        },
        ratingDraftRangeStyle() {
            const percentage = (value) => `${((value - 1) / 9) * 100}%`;
            return {
                '--library-rating-min': percentage(this.ratingDraftMinimumValue),
                '--library-rating-max': percentage(this.ratingDraftMaximumValue),
            };
        },
        hasActiveResultConstraint() {
            return Boolean(this.appliedFilters.q || this.appliedFilters.rating || this.hasActiveLibraryFilters);
        },
        activeLibraryFilterChips() {
            const filters = this.appliedLibraryFilters;
            const chips = [];
            if (filters.rating_min !== null || filters.rating_max !== null) {
                const label =
                    filters.rating_min !== null && filters.rating_max !== null
                        ? `Оцінка ${filters.rating_min}–${filters.rating_max}`
                        : filters.rating_min !== null
                          ? `Оцінка від ${filters.rating_min}`
                          : `Оцінка до ${filters.rating_max}`;
                chips.push({ key: 'rating', label });
            }
            if (filters.year_from !== null || filters.year_to !== null) {
                const label =
                    filters.year_from !== null && filters.year_to !== null
                        ? `${filters.year_from}–${filters.year_to}`
                        : filters.year_from !== null
                          ? `Від ${filters.year_from}`
                          : `До ${filters.year_to}`;
                chips.push({ key: 'period', label });
            }
            if (filters.without_year) chips.push({ key: 'without_year', label: 'Без року' });
            if (filters.paused_only) chips.push({ key: 'paused_only', label: 'Відкладене' });
            return chips;
        },
        activeLibraryFilterCount() {
            return this.activeLibraryFilterChips.length;
        },
        hasActiveLibraryFilters() {
            return this.activeLibraryFilterCount > 0;
        },
        shelfNavigation() {
            return [
                { status: '', countKey: 'total', label: 'Усі' },
                { status: 'want_to_read', countKey: 'want_to_read', label: 'Хочу прочитати' },
                { status: 'reading', countKey: 'reading', label: 'Читаю' },
                { status: 'read', countKey: 'read', label: 'Прочитано' },
                { status: 'abandoned', countKey: 'abandoned', label: 'Не дочитано' },
            ];
        },
        shelfRows() {
            const rows = [];
            for (let index = 0; index < this.books.length; index += this.shelfColumns) {
                rows.push(this.books.slice(index, index + this.shelfColumns));
            }
            return rows;
        },
        drawerBusy() {
            return this.saving || this.readingMutationState.savingReadingId !== null;
        },
        sortDirectionLabel() {
            if (this.ownerSortBy === 'title') return this.ownerSortDirection === 'asc' ? 'Від А до Я' : 'Від Я до А';
            if (this.ownerSortBy === 'added_at') return this.ownerSortDirection === 'asc' ? 'Давно додані спочатку' : 'Нещодавно додані спочатку';
            return this.ownerSortDirection === 'asc' ? 'Старіша активність спочатку' : 'Новіша активність спочатку';
        },
        publicSortDirectionLabel() {
            if (this.publicSortBy === 'title') return this.publicSortDirection === 'asc' ? 'Від А до Я' : 'Від Я до А';
            if (this.publicSortBy === 'added_at') return this.publicSortDirection === 'asc' ? 'Давно додані спочатку' : 'Нещодавно додані спочатку';
            return this.publicSortDirection === 'asc' ? 'Старіша активність спочатку' : 'Новіша активність спочатку';
        },
    },
    watch: {
        'viewer.id'() {
            this.$refs.quickPreview?.close({ restoreFocus: false });
            this.viewerContexts = {};
            this.previewBookFacts = {};
            this.viewerContextGeneration += 1;
            if (!this.isOwner) this.refreshViewerContexts();
        },
        'profile.username'(next, previous) {
            if (next === previous) return;
            if (this.isOwner) this.resetLibraryContext();
            else {
                this.$refs.quickPreview?.close({ restoreFocus: false });
                this.viewerContextGeneration += 1;
                this.viewerContexts = {};
                this.previewBookFacts = {};
                this.viewerContextState = 'idle';
                this.books = [];
                this.publicLibraryLoaded = false;
                this.loadBooks();
            }
        },
        isOwner(next, previous) {
            if (next !== previous) this.resetLibraryContext();
        },
    },
    async mounted() {
        if (this.drawerOnly) return;
        if (this.isOwner) this.ownerViewMode = this.readOwnerViewMode();
        window.addEventListener?.('resize', this.updateShelfColumns);
        this.$nextTick(() => {
            const layout = this.$refs.shelfLayout;
            if (layout && window.ResizeObserver) {
                this.shelfResizeObserver = new window.ResizeObserver(this.updateShelfColumns);
                this.shelfResizeObserver.observe(layout);
            }
            this.updateShelfColumns();
        });
        const generation = this.booksLoadGeneration + 1;
        await this.applyFilters();
        if (this.isOwner && generation === this.booksLoadGeneration) await this.openDeepLinkedManagement();
    },
    beforeDestroy() {
        window.removeEventListener?.('resize', this.updateShelfColumns);
        this.shelfResizeObserver?.disconnect();
        this.booksLoadGeneration += 1;
        this.membershipLoadGeneration += 1;
        this.historyLoadGeneration += 1;
        this.historyPending = {};
        this.historyPendingAuthority = {};
        this.historyAuthority = {};
    },
    methods: {
        viewStorageKey() {
            const id = Number(this.profile?.id);
            return this.isOwner && Number.isSafeInteger(id) && id > 0 ? `foxy:library-view:${id}` : null;
        },
        readOwnerViewMode() {
            const key = this.viewStorageKey();
            if (!key) return 'cards';
            try {
                return window.localStorage?.getItem(key) === 'shelves' ? 'shelves' : 'cards';
            } catch {
                return 'cards';
            }
        },
        setOwnerViewMode(mode) {
            if (!this.isOwner || !['cards', 'shelves'].includes(mode) || this.ownerViewMode === mode) return;
            this.$refs.quickPreview?.close({ restoreFocus: false });
            this.ownerViewMode = mode;
            const key = this.viewStorageKey();
            if (key) {
                try {
                    window.localStorage?.setItem(key, mode);
                } catch {
                    // Browser storage can be disabled; the current view still works.
                }
            }
            if (mode === 'shelves') this.$nextTick(this.updateShelfColumns);
        },
        updateShelfColumns() {
            const width = this.$refs.shelfLayout?.clientWidth || 0;
            if (width > 0) this.shelfColumns = Math.min(6, Math.max(2, Math.floor((width + 16) / 140)));
        },
        resolveBookCover(cover) {
            return cover || '/images/placeholders/book-cover.svg';
        },
        onCoverError(event) {
            const fallback = '/images/placeholders/book-cover.svg';
            if (event?.target && !String(event.target.src || '').includes(fallback)) {
                event.target.src = fallback;
            }
        },
        statusLabel(status) {
            return bookStatusPresentation(status).label;
        },
        languageLabel(code) {
            if (!code) return '—';
            const normalized = String(code).toLowerCase();
            if (!ISO6391.validate(normalized)) return normalized.toUpperCase();
            return ISO6391.getNativeName(normalized) || ISO6391.getName(normalized) || normalized.toUpperCase();
        },
        canonicalLibraryFilterSnapshot(source) {
            const optionalInteger = (value, minimum, maximum, label) => {
                if (value === null || value === undefined || String(value).trim() === '') return null;
                const normalized = Number(value);
                if (!Number.isInteger(normalized) || normalized < minimum || normalized > maximum) {
                    throw new Error(label);
                }
                return normalized;
            };
            const snapshot = {
                rating_min: optionalInteger(source.rating_min, 1, 10, 'Оцінка має бути цілим числом від 1 до 10.'),
                rating_max: optionalInteger(source.rating_max, 1, 10, 'Оцінка має бути цілим числом від 1 до 10.'),
                year_from: optionalInteger(source.year_from, 1000, 9999, 'Рік має бути цілим числом від 1000 до 9999.'),
                year_to: optionalInteger(source.year_to, 1000, 9999, 'Рік має бути цілим числом від 1000 до 9999.'),
                without_year: Boolean(source.without_year),
                paused_only: Boolean(source.paused_only),
            };
            if (snapshot.rating_min !== null && snapshot.rating_max !== null && snapshot.rating_min > snapshot.rating_max) {
                throw new Error('Мінімальна оцінка не може перевищувати максимальну.');
            }
            if (snapshot.year_from !== null && snapshot.year_to !== null && snapshot.year_from > snapshot.year_to) {
                throw new Error('Початковий рік не може перевищувати кінцевий.');
            }
            return snapshot;
        },
        clearLibraryFilterError() {
            this.filterDraftError = '';
        },
        updateRatingDraftMinimum(value) {
            const minimum = Math.max(1, Math.min(10, Number(value)));
            const currentMaximum = Number(this.filterDraft.rating_max);
            const maximum = Number.isInteger(currentMaximum) && currentMaximum >= 1 && currentMaximum <= 10 ? currentMaximum : 10;
            this.filterDraft.rating_min = Math.min(minimum, maximum);
            this.filterDraft.rating_max = maximum;
            this.clearLibraryFilterError();
        },
        updateRatingDraftMaximum(value) {
            const maximum = Math.max(1, Math.min(10, Number(value)));
            const currentMinimum = Number(this.filterDraft.rating_min);
            const minimum = Number.isInteger(currentMinimum) && currentMinimum >= 1 && currentMinimum <= 10 ? currentMinimum : 1;
            this.filterDraft.rating_min = minimum;
            this.filterDraft.rating_max = Math.max(maximum, minimum);
            this.clearLibraryFilterError();
        },
        openLibraryFilters() {
            this.filterDraft = { ...this.appliedLibraryFilters };
            this.filterDraftError = '';
            this.filtersPanelOpen = true;
            this.$nextTick(() => this.$refs.libraryFilterPanelTitle?.focus?.());
        },
        cancelLibraryFilters() {
            this.filterDraft = { ...this.appliedLibraryFilters };
            this.filterDraftError = '';
            this.filtersPanelOpen = false;
        },
        toggleLibraryFilters() {
            if (this.filtersPanelOpen) this.cancelLibraryFilters();
            else this.openLibraryFilters();
        },
        resetLibraryFilterDraft() {
            this.filterDraft = emptyLibraryFilters();
            this.filterDraftError = '';
        },
        applyLibraryFilters() {
            try {
                const snapshot = this.canonicalLibraryFilterSnapshot(this.filterDraft);
                this.appliedLibraryFilters = snapshot;
                this.filterDraft = { ...snapshot };
                this.filterDraftError = '';
                this.filtersPanelOpen = false;
                return this.loadBooks(1);
            } catch (error) {
                this.filterDraftError = error?.message || 'Перевірте значення фільтрів.';
                return undefined;
            }
        },
        removeLibraryFilter(key) {
            const next = { ...this.appliedLibraryFilters };
            if (key === 'rating') {
                next.rating_min = null;
                next.rating_max = null;
            } else if (key === 'period') {
                next.year_from = null;
                next.year_to = null;
            } else if (key === 'without_year') {
                next.without_year = false;
            } else if (key === 'paused_only') {
                next.paused_only = false;
            } else {
                return undefined;
            }
            this.appliedLibraryFilters = next;
            this.filterDraft = { ...next };
            return this.loadBooks(1);
        },
        clearAllLibraryFilters() {
            if (!this.hasActiveLibraryFilters) return undefined;
            this.appliedLibraryFilters = emptyLibraryFilters();
            this.filterDraft = emptyLibraryFilters();
            this.filterDraftError = '';
            this.filtersPanelOpen = false;
            return this.loadBooks(1);
        },
        applyFilters() {
            this.appliedFilters = { q: this.filters.q.trim(), status: this.filters.status, rating: this.isOwner ? this.filters.rating || '' : '' };
            return this.loadBooks(1);
        },
        selectShelf(status) {
            this.filters.status = status;
            return this.applyFilters();
        },
        changeSortCriterion() {
            return this.applyFilters();
        },
        toggleSortDirection() {
            this.ownerSortDirection = this.ownerSortDirection === 'desc' ? 'asc' : 'desc';
            return this.applyFilters();
        },
        togglePublicSortDirection() {
            this.publicSortDirection = this.publicSortDirection === 'desc' ? 'asc' : 'desc';
            return this.applyFilters();
        },
        clearSearch() {
            if (!this.filters.q && !this.appliedFilters.q) return;
            this.filters.q = '';
            return this.applyFilters();
        },
        refreshBooks({ ownerFailureMessage = '' } = {}) {
            if (this.drawerOnly) {
                if (this.refreshExternalContext) return this.refreshExternalContext();
                this.$emit('management-refresh');
                return Promise.resolve();
            }
            return this.loadBooks(this.isOwner ? this.requestedOwnerPage : 1, { ownerFailureMessage });
        },
        resetLibraryContext() {
            this.viewerContextGeneration += 1;
            this.viewerContexts = {};
            this.previewBookFacts = {};
            this.viewerContextState = 'idle';
            this.booksLoadGeneration += 1;
            this.requestedOwnerPage = 1;
            this.$refs.quickPreview?.close({ restoreFocus: false });
            this.closeActionModal();
            this.historyCache = {};
            this.historyPending = {};
            this.historyPendingAuthority = {};
            this.historyAuthority = {};
            this.languagePreferencesState = { status: 'idle', data: null, error: '' };
            this.books = [];
            this.pagination = { current_page: 1, last_page: 1, total: 0 };
            this.libraryCounts = { total: 0, want_to_read: 0, reading: 0, read: 0, abandoned: 0 };
            this.ownerSortBy = 'activity';
            this.ownerSortDirection = 'desc';
            this.publicSortBy = 'activity';
            this.publicSortDirection = 'desc';
            this.publicLibraryLoaded = false;
            this.ownerViewMode = this.readOwnerViewMode();
            this.ownerLibraryLoadState = this.isOwner ? 'idle' : 'loaded';
            this.ownerLibraryLoadError = '';
            return this.resetFilters();
        },
        retryOwnerLibrary() {
            if (!this.isOwner || this.loading) return;
            return this.loadBooks(this.requestedOwnerPage);
        },
        async loadBooks(page = 1, { ownerFailureMessage = '' } = {}) {
            const generation = ++this.booksLoadGeneration;
            const owner = this.isOwner;
            let requestedPage = Number.isInteger(page) && page > 0 ? page : 1;
            // Track intent before requesting; pagination still describes the settled response.
            if (owner) this.requestedOwnerPage = requestedPage;
            const username = this.profile?.username;
            const current = () => generation === this.booksLoadGeneration && owner === this.isOwner && username === this.profile?.username;
            this.loading = true;
            if (!owner) {
                this.$refs.quickPreview?.close({ restoreFocus: false });
                this.viewerContextGeneration += 1;
                this.viewerContexts = {};
                this.previewBookFacts = {};
                this.viewerContextState = 'idle';
            }
            if (owner) {
                this.ownerLibraryLoadState = 'loading';
                this.ownerLibraryLoadError = '';
            }
            try {
                let data;
                if (owner) {
                    const filters = { q: this.appliedFilters.q || undefined, status: this.appliedFilters.status || undefined };
                    if (this.appliedFilters.rating) filters.rating = this.appliedFilters.rating;
                    for (const field of ['rating_min', 'rating_max', 'year_from', 'year_to']) {
                        if (this.appliedLibraryFilters[field] !== null) filters[field] = this.appliedLibraryFilters[field];
                    }
                    if (this.appliedLibraryFilters.without_year) filters.without_year = 1;
                    if (this.appliedLibraryFilters.paused_only) filters.paused_only = 1;
                    if (this.ownerSortBy !== 'activity' || this.ownerSortDirection !== 'desc') {
                        filters.sort_by = this.ownerSortBy;
                        filters.sort_direction = this.ownerSortDirection;
                    }
                    const fetchPage = (number) => axios.get('/api/account/library-statuses', { params: { ...filters, page: number } });
                    ({ data } = await fetchPage(requestedPage));
                    if (!current()) return;
                    // A mutation can remove the last page. Recover once within this request generation.
                    if (Number.isInteger(data?.meta?.last_page) && requestedPage > data.meta.last_page) {
                        requestedPage = Math.max(1, data.meta.last_page);
                        this.requestedOwnerPage = requestedPage;
                        ({ data } = await fetchPage(requestedPage));
                    }
                } else {
                    if (!username) return;
                    ({ data } = await axios.get(`/api/account/profile/${encodeURIComponent(username)}/library`, {
                        params: {
                            q: this.appliedFilters.q || undefined,
                            status: this.appliedFilters.status || undefined,
                            sort_by: this.publicSortBy,
                            sort_direction: this.publicSortDirection,
                            page: Number.isInteger(page) && page > 0 ? page : 1,
                        },
                    }));
                }
                if (!current()) return;
                if (owner) this.requestedOwnerPage = requestedPage;
                this.books = data?.books || [];
                this.pagination = data?.meta || { current_page: 1, last_page: 1, total: 0 };
                this.libraryCounts = data?.library_counts || { total: 0, want_to_read: 0, reading: 0, read: 0, abandoned: 0 };
                if (owner) {
                    this.ownerLibraryLoadState = 'loaded';
                } else {
                    this.publicLibraryLoaded = true;
                    await this.refreshViewerContexts();
                }
            } catch (e) {
                if (!current()) return;
                const message = (owner && ownerFailureMessage) || e?.response?.data?.message || 'Не вдалося завантажити бібліотеку.';
                this.books = [];
                if (owner) {
                    this.pagination = null;
                    this.ownerLibraryLoadState = 'error';
                    this.ownerLibraryLoadError = message;
                } else {
                    this.pagination = { current_page: 1, last_page: 1, total: 0 };
                    alert(message);
                }
            } finally {
                if (current()) this.loading = false;
            }
        },
        contextFor(item) {
            return normalizeBookCardContext(item);
        },
        publicPreviewItem(item) {
            if (!canUseBookQuickPreview(Boolean(this.viewer)) || !item?.book?.id) return null;
            const context = this.viewerContexts[item.book.id];
            if (!context || (this.viewer && ['idle', 'error'].includes(this.viewerContextState))) return null;
            // Public card is never mutated or passed to the Preview normalizer.
            const book = { ...item.book, ...(this.viewer ? this.previewBookFacts[item.book.id] : {}) };
            return { id: item.book.id, book, status: context.shelf_status, card_context: context };
        },
        async refreshViewerContexts() {
            if (this.isOwner) return;
            const generation = ++this.viewerContextGeneration;
            const pageGeneration = this.booksLoadGeneration;
            const viewerId = this.viewer?.id;
            const username = this.profile?.username;
            const current = () =>
                generation === this.viewerContextGeneration &&
                pageGeneration === this.booksLoadGeneration &&
                viewerId === this.viewer?.id &&
                username === this.profile?.username &&
                !this.isOwner;
            const bookIds = this.books.map((item) => item.book?.id).filter(Boolean);
            if (!viewerId || !bookIds.length) {
                this.viewerContexts = {};
                this.previewBookFacts = {};
                this.viewerContextState = 'loaded';
                return;
            }
            this.viewerContextState = 'loading';
            try {
                const { data } = await axios.get('/api/account/book-preview-contexts', { params: { book_ids: bookIds } });
                if (!current()) return;
                if (
                    Number(data?.viewer_id) !== Number(viewerId) ||
                    !data?.viewer_contexts ||
                    bookIds.some((id) => !Object.prototype.hasOwnProperty.call(data.viewer_contexts, id))
                ) {
                    throw new Error('Invalid viewer snapshot');
                }
                this.viewerContexts = data.viewer_contexts;
                this.previewBookFacts = data.book_facts || {};
                this.viewerContextState = 'loaded';
            } catch {
                if (!current()) return;
                this.viewerContexts = {};
                this.previewBookFacts = {};
                this.viewerContextState = 'error';
                // A failed hydration must not turn foreign card facts into viewer state.
                this.$refs.quickPreview?.close({ restoreFocus: false });
            }
        },
        managementLink(item, section = null) {
            const context = this.contextFor(item);
            if (!section && context.managementUrl) return context.managementUrl;
            return bookManagementLink(this.profile?.username, context.readingStatusId, section);
        },
        personalRating(item) {
            const rating = this.isOwner ? this.contextFor(item).reading?.rating : item?.rating;
            return Number.isInteger(rating) && rating >= 1 && rating <= 10 ? rating : null;
        },
        completionDateLabel(item) {
            const context = this.contextFor(item);
            const reading = context.reading;
            if (context.shelfStatus !== 'read' || reading?.state !== 'completed') return '';
            return typeof reading.display_date_label === 'string' ? reading.display_date_label : '';
        },
        membershipDate(item) {
            if (!this.isOwner || typeof item?.effective_library_added_at !== 'string') return null;
            const instant = new Date(item.effective_library_added_at);
            if (Number.isNaN(instant.getTime())) return null;
            const visual = new Intl.DateTimeFormat('uk-UA', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            }).format(instant);
            const spoken = new Intl.DateTimeFormat('uk-UA', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            }).format(instant);

            return { visual, accessible: `Додано до бібліотеки: ${spoken}` };
        },
        cardMetadata(item) {
            const context = this.contextFor(item);
            const reading = context.reading;
            if (!reading) return [];
            const languageLabel = this.languageSummary(reading.languages);
            const formatLabel = this.formatSummary(reading.formats);
            const label = [languageLabel, formatLabel].filter(Boolean).join(' · ');
            return label ? [{ key: 'reading-context', label, title: label }] : [];
        },
        languageSummary(languages) {
            if (!Array.isArray(languages) || languages.length === 0) return '';
            if (languages.length === 1) return languages[0]?.label || '';
            return this.languageCountLabel(languages.length);
        },
        languageCountLabel(count) {
            return `${count} ${this.ukrainianPlural(count, 'мова', 'мови', 'мов')}`;
        },
        formatSummary(formats) {
            if (!Array.isArray(formats) || formats.length === 0) return '';
            if (formats.length === 1) return formats[0]?.label || '';
            return this.formatCountLabel(formats.length);
        },
        formatCountLabel(count) {
            return `${count} ${this.ukrainianPlural(count, 'формат', 'формати', 'форматів')}`;
        },
        ukrainianPlural(count, one, few, many) {
            const lastTwoDigits = Math.abs(Number(count)) % 100;
            const lastDigit = lastTwoDigits % 10;
            if (lastTwoDigits > 10 && lastTwoDigits < 20) return many;
            if (lastDigit === 1) return one;
            if (lastDigit >= 2 && lastDigit <= 4) return few;
            return many;
        },
        openCardPreview(item, event, immediate = false) {
            const previewItem = this.isOwner ? item : this.publicPreviewItem(item);
            if (!previewItem) return;
            this.$refs.quickPreview?.scheduleOpen(previewItem, event?.currentTarget, immediate);
        },
        schedulePreviewClose() {
            this.$refs.quickPreview?.scheduleClose();
        },
        handlePreviewTriggerKeydown(event, item) {
            const previewItem = this.isOwner ? item : this.publicPreviewItem(item);
            if (previewItem) this.$refs.quickPreview?.handleTriggerKeydown(event, previewItem, event?.currentTarget);
        },
        isPreviewOpen(item) {
            return Number(this.previewOpenItemId) === Number(this.isOwner ? item?.id : item?.book?.id);
        },
        handlePreviewManage({ section = null } = {}) {
            const preview = this.$refs.quickPreview;
            const item = preview?.activeItem;
            const trigger = preview?.getTrigger();
            if (!item) return;
            if (!this.isOwner) {
                const context = normalizeBookCardContext(item);
                if (!this.viewer || !context.owned || !context.readingStatusId) return;
                preview.prepareFocusReturn(trigger);
                preview.close({ restoreFocus: false });
                this.$refs.viewerManagement?.openExternalManagement({ reading_status_id: context.readingStatusId, section }, trigger);
                return;
            }
            preview.close({ restoreFocus: false });
            this.openAction(item, { currentTarget: trigger, detail: 0 }, section);
        },
        async handlePreviewRemove() {
            const preview = this.$refs.quickPreview;
            const item = preview?.activeItem;
            const trigger = preview?.getTrigger();
            if (!item) return;
            if (!this.isOwner) {
                const context = normalizeBookCardContext(item);
                if (preview.busy || !context.quickRemovalAllowed || !context.readingStatusId) return;
                const generation = this.viewerContextGeneration;
                const operation = preview.operationGeneration;
                preview.busy = true;
                try {
                    const result = await requestLibraryRemoval(
                        (payload) => axios.delete(`/api/account/reading-status/${context.readingStatusId}`, { data: payload }),
                        {
                            lockVersion: context.readingStatusLockVersion,
                            openReadingInstanceId: null,
                            openReadingState: null,
                        },
                    );
                    if (generation === this.viewerContextGeneration && (result.completed || result.stale)) await this.refreshViewerContexts();
                } catch (error) {
                    if (generation === this.viewerContextGeneration) {
                        await this.refreshViewerContexts();
                        preview.error = error?.response?.data?.message || 'Не вдалося прибрати книгу з вашої бібліотеки.';
                    }
                } finally {
                    if (operation === preview.operationGeneration) preview.busy = false;
                }
                return;
            }
            preview.close({ restoreFocus: false });
            this.quickRemove(item, trigger);
        },
        async quickRemove(item, trigger = null) {
            const context = this.contextFor(item);
            if (!context.quickRemovalAllowed || !context.readingStatusId) return;
            const statusId = context.readingStatusId;
            try {
                const result = await requestLibraryRemoval((payload) => axios.delete(`/api/account/reading-status/${statusId}`, { data: payload }), {
                    lockVersion: context.readingStatusLockVersion,
                    openReadingInstanceId: null,
                    openReadingState: null,
                });
                if (result.stale) {
                    this.invalidateHistory(statusId);
                    alert('Статус читання змінився. Оновлюємо дані перед повторною дією.');
                    await this.refreshBooks();
                    return;
                }
                if (!result.completed) {
                    if (trigger?.isConnected) trigger.focus();
                    return;
                }
                this.invalidateHistory(statusId);
                this.syncBookStatusCache(item?.book?.id, null);
                await this.refreshBooks({ ownerFailureMessage: committedMutationRefreshError });
                this.$nextTick(() => this.$refs.libraryFocusTarget?.focus());
            } catch (error) {
                alert(error?.response?.data?.message || 'Не вдалося прибрати книгу з бібліотеки.');
                if (trigger?.isConnected) trigger.focus();
            }
        },
        claimHistoryAuthority(statusId) {
            const authority = ++this.historyAuthoritySequence;
            this.historyAuthority[statusId] = authority;
            return authority;
        },
        ownsHistoryAuthority(statusId, authority) {
            return this.historyAuthority[statusId] === authority;
        },
        async openDeepLinkedManagement() {
            if (!this.isOwner) return;
            // Both manage and the history route identify BookReadingStatus, not Book.
            const requestedId = Number(this.$route?.query?.manage || 0);
            if (!Number.isInteger(requestedId) || requestedId <= 0) return;
            const section = this.$route?.query?.section === 'collections' ? 'collections' : null;
            const requestedReadingId = Number(this.$route?.query?.reading || 0);
            const initialReadingInstanceId = Number.isInteger(requestedReadingId) && requestedReadingId > 0 ? requestedReadingId : null;
            await this.openManagementByStatus(
                requestedId,
                initialReadingInstanceId,
                this.$refs.libraryFocusTarget,
                section,
                () => requestedId === Number(this.$route?.query?.manage),
            );
        },
        async openExternalManagement(selection, trigger = null) {
            if (!this.drawerOnly || !selection) return;
            const requestedId = Number(selection.reading_status_id);
            const requestedReadingId = Number(selection.reading_instance_id);
            const initialReadingInstanceId = Number.isInteger(requestedReadingId) && requestedReadingId > 0 ? requestedReadingId : null;
            if (initialReadingInstanceId) {
                await this.openManagementByStatus(requestedId, initialReadingInstanceId, trigger, null, null, true);
                return;
            }
            await this.openManagementByStatus(requestedId, null, trigger, selection.section || null, null, false);
        },
        async openManagementByStatus(
            requestedId,
            initialReadingInstanceId = null,
            trigger = null,
            section = null,
            stillCurrent = null,
            openInitialReadingEditor = false,
        ) {
            if (!this.isOwner || !Number.isInteger(requestedId) || requestedId <= 0) return;
            const item = this.books.find((book) => Number(book.id) === requestedId);
            if (item) {
                this.openAction(item, { currentTarget: trigger, detail: 1 }, section, initialReadingInstanceId, openInitialReadingEditor);
                return;
            }
            const generation = ++this.membershipLoadGeneration;
            const username = this.profile?.username;
            const authority = this.claimHistoryAuthority(requestedId);
            const current = () =>
                generation === this.membershipLoadGeneration &&
                this.isOwner &&
                username === this.profile?.username &&
                (!stillCurrent || stillCurrent());
            try {
                const { data } = await axios.get(`/api/account/reading-status/${requestedId}/history`);
                if (
                    !current() ||
                    !this.ownsHistoryAuthority(requestedId, authority) ||
                    !this.hasHistoryEnvelope(data) ||
                    Number(data.reading_status.id) !== requestedId ||
                    !data.book
                )
                    return;
                this.historyCache[requestedId] = data;
                const target = {
                    ...data.reading_status,
                    book: data.book,
                    open_reading_instance_id: data.current_reading?.id ?? null,
                    open_reading_state: data.current_reading?.state ?? null,
                };
                this.openAction(target, { currentTarget: trigger, detail: 1 }, section, initialReadingInstanceId, openInitialReadingEditor);
            } catch (error) {
                if (!current() || !this.ownsHistoryAuthority(requestedId, authority)) return;
                alert(error?.response?.data?.message || 'Не вдалося завантажити історію читання.');
            }
        },
        resetFilters() {
            this.ownerSortBy = 'activity';
            this.ownerSortDirection = 'desc';
            this.filtersPanelOpen = false;
            this.filterDraft = emptyLibraryFilters();
            this.appliedLibraryFilters = emptyLibraryFilters();
            this.filterDraftError = '';
            this.filters = {
                q: '',
                status: '',
                rating: '',
            };
            return this.applyFilters();
        },
        openAction(item, event = null, initialSection = null, initialReadingInstanceId = null, openInitialReadingEditor = false) {
            if (!this.isOwner) return;
            const itemHasStatus = Object.prototype.hasOwnProperty.call(item || {}, 'status');
            this.actionModalOpenedByKeyboard = event?.detail === 0;
            this.$refs.profileEditShell?.setReturnFocusTarget(event?.currentTarget || null);
            const generation = ++this.membershipLoadGeneration;
            ++this.historyLoadGeneration;
            this.actionModal = {
                id: item?.id || null,
                title: item?.book?.title || 'Книга',
                subtitle: 'Керування статусом, добірками та діями «Видалити прочитання».',
                bookUrl: item?.book?.slug ? `/books/${item.book.slug}` : '',
                bookSlug: item?.book?.slug || '',
                book: item?.book
                    ? {
                          id: item.book.id,
                          slug: item.book.slug,
                          title: item.book.title,
                          cover: item.book.cover,
                      }
                    : null,
                openReadingInstanceId: item?.open_reading_instance_id ?? null,
                openReadingState: item?.open_reading_state ?? null,
                initialSection,
                initialReadingInstanceId,
                openInitialReadingEditor,
            };
            this.statusSelectionDirty = false;
            this.editForm = {
                status: itemHasStatus ? item.status : null,
                times_read: item?.times_read ?? 0,
                sessions: (item?.sessions || []).map((s) => ({
                    read_at: s.read_at || '',
                    language: s.language || '',
                })),
                reading_instances: (item?.reading_instances || []).map((reading) => ({
                    id: reading.id,
                    display_label: reading.display_label,
                })),
                library_ids: [],
                lock_version: item?.lock_version ?? null,
            };
            this.resetMembershipState();
            this.membershipsLoading = true;
            this.loadHistory(this.actionModal.id);
            this.loadLibrariesAndMembership(item, generation);
            this.loadReadingLanguagePreferences();
            if (initialSection === 'collections') this.$nextTick(() => this.$refs.historyManager?.focusCollections());
        },
        async requestCloseActionModal() {
            if (this.drawerBusy) return;
            const manager = this.$refs.historyManager;
            if (manager && !(await manager.requestDiscardActiveReadingEdit())) return;
            this.closeActionModal();
        },
        closeActionModal() {
            ++this.membershipLoadGeneration;
            ++this.historyLoadGeneration;
            this.actionModal = null;
            this.statusSelectionDirty = false;
            this.resetMembershipState();
            this.resetHistoryState();
            this.resetReadingMutationState();
        },
        resetMembershipState() {
            this.editForm.library_ids = [];
            this.membershipSnapshot = null;
            this.membershipsLoaded = false;
            this.membershipsLoading = false;
            this.membershipLoadFailed = false;
            this.membershipConflict = null;
            this.membershipRecoveryError = '';
            this.membershipRecoveryLoading = false;
        },
        resetHistoryState() {
            this.historyState = {
                status: 'idle',
                data: null,
                error: '',
            };
        },
        resetReadingMutationState() {
            this.readingMutationState = {
                savingReadingId: null,
                errors: emptyReadingErrors(),
                message: '',
                messageType: '',
            };
        },
        clearReadingErrors() {
            this.readingMutationState = {
                ...this.readingMutationState,
                errors: emptyReadingErrors(),
                message: '',
                messageType: '',
            };
        },
        async loadReadingLanguagePreferences(force = false) {
            if (!this.isOwner) return null;
            if (!force && this.languagePreferencesState.status === 'loaded') {
                return this.languagePreferencesState.data;
            }

            this.languagePreferencesState = {
                status: 'loading',
                data: this.languagePreferencesState.data,
                error: '',
            };
            try {
                const preferences = await readingLanguagePreferences.load({ force });
                this.languagePreferencesState = { status: 'loaded', data: preferences, error: '' };
                return preferences;
            } catch {
                this.languagePreferencesState = {
                    status: 'error',
                    data: null,
                    error: 'Не вдалося завантажити мови читання.',
                };
                return null;
            }
        },
        loadLibraryCatalog(force = false) {
            if (!force && this.libraryCatalogLoaded) return Promise.resolve(this.userLibraries);
            if (!force && this.libraryCatalogPending) return this.libraryCatalogPending;

            const request = axios.get('/api/account/libraries').then(({ data }) => {
                return data?.libraries || [];
            });
            this.libraryCatalogPending = request;

            return request.finally(() => {
                if (this.libraryCatalogPending === request) this.libraryCatalogPending = null;
            });
        },
        isCurrentMembershipLoad(generation, statusId, bookSlug) {
            return (
                generation === this.membershipLoadGeneration &&
                this.actionModal !== null &&
                this.actionModal.id === statusId &&
                this.actionModal.bookSlug === bookSlug
            );
        },
        async loadLibrariesAndMembership(item, generation, { forceCatalog = false, recovery = false } = {}) {
            const statusId = item?.id || null;
            const bookSlug = item?.book?.slug || '';
            const catalogRequest = this.loadLibraryCatalog(forceCatalog);
            if (!recovery) {
                catalogRequest
                    .then((libraries) => {
                        if (!this.isCurrentMembershipLoad(generation, statusId, bookSlug)) return;
                        this.userLibraries = libraries;
                        this.libraryCatalogLoaded = true;
                    })
                    .catch(() => {});
            }
            try {
                const [libraries, { data: inLibs }] = await Promise.all([
                    catalogRequest,
                    bookSlug ? axios.get(`/api/books/${bookSlug}/libraries`) : Promise.resolve({ data: { libraries: [] } }),
                ]);
                if (!this.isCurrentMembershipLoad(generation, statusId, bookSlug)) return;
                if (!Array.isArray(inLibs?.library_ids) || typeof inLibs?.membership_snapshot !== 'string') {
                    throw new Error('Canonical collection membership state is missing.');
                }
                this.userLibraries = libraries;
                this.libraryCatalogLoaded = true;
                this.editForm.library_ids = [...inLibs.library_ids];
                this.membershipSnapshot = inLibs.membership_snapshot;
                this.membershipsLoaded = true;
                this.membershipLoadFailed = false;
                this.membershipConflict = null;
                this.membershipRecoveryError = '';
            } catch {
                if (!this.isCurrentMembershipLoad(generation, statusId, bookSlug)) return;
                if (recovery) {
                    this.membershipRecoveryError = 'Не вдалося оновити добірки.';
                } else {
                    this.membershipSnapshot = null;
                    this.membershipsLoaded = false;
                    this.membershipLoadFailed = true;
                }
            } finally {
                if (this.isCurrentMembershipLoad(generation, statusId, bookSlug)) {
                    this.membershipsLoading = false;
                    this.membershipRecoveryLoading = false;
                }
            }
        },
        retryMemberships() {
            if (!this.actionModal) return;
            const generation = ++this.membershipLoadGeneration;
            this.membershipsLoading = true;
            this.membershipLoadFailed = false;
            this.loadLibrariesAndMembership(
                {
                    id: this.actionModal.id,
                    book: { slug: this.actionModal.bookSlug },
                },
                generation,
            );
        },
        recoverMemberships() {
            if (!this.actionModal || this.membershipRecoveryLoading) return;
            const generation = ++this.membershipLoadGeneration;
            this.membershipsLoading = true;
            this.membershipRecoveryLoading = true;
            this.membershipRecoveryError = '';
            this.loadLibrariesAndMembership(
                {
                    id: this.actionModal.id,
                    book: { slug: this.actionModal.bookSlug },
                },
                generation,
                { forceCatalog: true, recovery: true },
            );
        },
        isCurrentHistoryLoad(generation, statusId) {
            return generation === this.historyLoadGeneration && this.actionModal !== null && this.actionModal.id === statusId;
        },
        applyHistoryPayload(payload) {
            if (!payload || !this.actionModal) return;
            const status = payload.reading_status || {};
            const current = payload.current_reading || null;
            if (Object.prototype.hasOwnProperty.call(status, 'status') && !this.statusSelectionDirty) {
                this.editForm.status = status.status;
            }
            this.editForm.times_read = status.times_read ?? this.editForm.times_read;
            this.editForm.lock_version = status.lock_version ?? this.editForm.lock_version;
            this.actionModal.openReadingInstanceId = current?.id ?? null;
            this.actionModal.openReadingState = current?.state ?? null;
            if (payload.book) {
                this.actionModal.title = payload.book.title || this.actionModal.title;
                this.actionModal.bookUrl = payload.book.url || this.actionModal.bookUrl;
                this.actionModal.book = {
                    ...this.actionModal.book,
                    ...payload.book,
                };
            }
        },
        handleEditFormUpdate(form) {
            if (Object.prototype.hasOwnProperty.call(form || {}, 'status') && form.status !== this.editForm.status) {
                this.statusSelectionDirty = true;
            }
            this.editForm = form;
        },
        hasHistoryEnvelope(payload) {
            return (
                Boolean(payload?.reading_status) &&
                Array.isArray(payload?.history) &&
                Array.isArray(payload?.format_options) &&
                Object.prototype.hasOwnProperty.call(payload, 'current_reading')
            );
        },
        replaceHistoryPayload(statusId, payload, authority, generation) {
            if (!this.ownsHistoryAuthority(statusId, authority)) return null;
            if (!this.hasHistoryEnvelope(payload)) return false;
            this.historyCache[statusId] = payload;
            if (this.isCurrentHistoryLoad(generation, statusId)) {
                this.historyState = { status: 'loaded', data: payload, error: '' };
                this.applyHistoryPayload(payload);
            }
            this.syncLibraryItemFromHistory(statusId, payload);
            return true;
        },
        syncLibraryItemFromHistory(statusId, payload) {
            const canonical = payload?.reading_status;
            if (!canonical) return;
            this.books = this.books.map((item) =>
                Number(item.id) === Number(statusId)
                    ? {
                          ...item,
                          status: canonical.status,
                          times_read: canonical.times_read ?? item.times_read,
                          reading_language: canonical.reading_language ?? null,
                          rating: Object.prototype.hasOwnProperty.call(canonical, 'rating') ? canonical.rating : item.rating,
                          lock_version: canonical.lock_version ?? item.lock_version,
                      }
                    : item,
            );
            const bookId = payload?.book?.id || this.actionModal?.book?.id || null;
            this.syncBookStatusCache(bookId, {
                status: canonical.status,
                times_read: canonical.times_read ?? 0,
                reading_language: canonical.reading_language ?? null,
                rating: canonical.rating ?? null,
            });
        },
        readingFromHistory(payload, readingId) {
            if (Number(payload?.current_reading?.id) === Number(readingId)) return payload.current_reading;
            return (payload?.history || []).find((reading) => Number(reading.id) === Number(readingId)) || null;
        },
        mapReadingValidationErrors(error, submittedFormats = []) {
            const responseErrors = error?.response?.data?.errors || {};
            const messagesFor = (predicate) =>
                Object.entries(responseErrors)
                    .filter(([key]) => predicate(key))
                    .flatMap(([, messages]) => (Array.isArray(messages) ? messages : [messages]))
                    .filter(Boolean);
            const errors = emptyReadingErrors();
            errors.language.primary = messagesFor((key) => key === 'primary_language_code')[0] || '';
            errors.language.membership = messagesFor((key) => key === 'language_codes' || key.startsWith('language_codes.'))[0] || '';
            for (const dateKey of ['started', 'finished']) {
                errors[dateKey].group = messagesFor((key) => key === dateKey)[0] || '';
                for (const component of ['precision', 'year', 'month', 'day']) {
                    errors[dateKey][component] = messagesFor((key) => key === `${dateKey}.${component}`)[0] || '';
                }
            }
            errors.dateRange = messagesFor((key) => key === 'date_range')[0] || '';
            errors.rating = messagesFor((key) => key === 'rating')[0] || '';

            const identity = (code) =>
                String(code || '')
                    .trim()
                    .toLowerCase();
            for (const [key, rawMessages] of Object.entries(responseErrors)) {
                if (key === 'formats') {
                    errors.format.group = (Array.isArray(rawMessages) ? rawMessages : [rawMessages]).filter(Boolean)[0] || '';
                    continue;
                }
                const match = key.match(/^formats\.(\d+)(?:\.(code|custom_label))?$/);
                if (!match) continue;
                const code = identity(submittedFormats[Number(match[1])]?.code);
                const message = (Array.isArray(rawMessages) ? rawMessages : [rawMessages]).filter(Boolean)[0] || '';
                if (!code) {
                    errors.format.group ||= message;
                    continue;
                }
                errors.format.byCode[code] ||= { code: '', customLabel: '' };
                const target = match[2] === 'custom_label' ? 'customLabel' : 'code';
                errors.format.byCode[code][target] ||= message;
            }

            const knownKey = (key) =>
                key === 'primary_language_code' ||
                key === 'language_codes' ||
                key.startsWith('language_codes.') ||
                key === 'started' ||
                ['started.precision', 'started.year', 'started.month', 'started.day'].includes(key) ||
                key === 'finished' ||
                ['finished.precision', 'finished.year', 'finished.month', 'finished.day'].includes(key) ||
                key === 'date_range' ||
                key === 'rating' ||
                key === 'formats' ||
                /^formats\.\d+(?:\.(?:code|custom_label))?$/.test(key);
            errors.form = messagesFor((key) => !knownKey(key))[0] || '';
            if (!Object.values(responseErrors).length) {
                errors.form = error?.response?.data?.message || 'Не вдалося зберегти дані прочитання.';
            }
            return errors;
        },
        firstReadingValidationTarget(errors) {
            if (errors.language.primary) return 'language.primary';
            if (errors.language.membership) return 'language.membership';
            for (const dateKey of ['started', 'finished']) {
                for (const component of ['precision', 'year', 'month', 'day', 'group']) {
                    if (errors[dateKey][component]) return `${dateKey}.${component}`;
                }
            }
            if (errors.format.group) return 'format.group';
            for (const [code, itemErrors] of Object.entries(errors.format.byCode || {})) {
                if (itemErrors.code) return `format.${encodeURIComponent(code)}.code`;
                if (itemErrors.customLabel) return `format.${encodeURIComponent(code)}.customLabel`;
            }
            if (errors.rating) return 'rating';
            if (errors.dateRange) return 'dateRange';
            return 'form';
        },
        async createHistoricalReading(intent) {
            const statusId = this.actionModal?.id || null;
            const bookId = this.actionModal?.book?.id || this.actionModal?.book_id || null;
            const bookKey = this.actionModal?.bookSlug || this.actionModal?.book?.slug || null;
            const historyGeneration = this.historyLoadGeneration;
            if (!statusId || !bookId || !bookKey || this.drawerBusy) return;

            const payload = {
                expected_reading_status_id: statusId,
                expected_reading_status_lock_version: this.historyState.data?.reading_status?.lock_version,
                result: intent.result,
                started: intent.started,
                finished: intent.finished,
                language_codes: intent.languageCodes,
                primary_language_code: intent.primaryLanguageCode,
                formats: intent.formats,
                rating: intent.rating,
            };
            const mutationAuthority = this.claimHistoryAuthority(statusId);
            this.readingMutationState = {
                savingReadingId: 'historical-create',
                errors: emptyReadingErrors(),
                message: '',
                messageType: '',
            };

            try {
                const response = await axios.post(`/api/account/books/${encodeURIComponent(bookKey)}/reading-history`, payload);
                const replaced = this.replaceHistoryPayload(statusId, response.data, mutationAuthority, historyGeneration);
                if (replaced === null) {
                    if (this.readingMutationState.savingReadingId === 'historical-create') {
                        this.readingMutationState = { ...this.readingMutationState, savingReadingId: null };
                    }
                    return;
                }
                if (!replaced) throw new Error('Canonical owner history is missing from the server response.');
                this.readingMutationState = {
                    savingReadingId: null,
                    errors: emptyReadingErrors(),
                    message: response.data?.message || 'Минуле прочитання додано.',
                    messageType: 'success',
                };
                this.$refs.historyManager?.completeHistoricalCreate();
                await this.refreshBooks({ ownerFailureMessage: committedMutationRefreshError });
            } catch (error) {
                const response = error?.response;
                if (response?.status === 409) {
                    const conflictPayload = response.data || {};
                    const replaced = this.replaceHistoryPayload(statusId, conflictPayload, mutationAuthority, historyGeneration);
                    if (replaced === null) {
                        if (this.readingMutationState.savingReadingId === 'historical-create') {
                            this.readingMutationState = { ...this.readingMutationState, savingReadingId: null };
                        }
                        return;
                    }
                    if (replaced === false) {
                        this.invalidateHistory(statusId);
                        await this.loadHistory(statusId, { force: true });
                    }
                    this.readingMutationState = {
                        savingReadingId: null,
                        errors: { ...emptyReadingErrors(), form: conflictPayload.message || 'Історія змінилася. Оновіть дані та повторіть дію.' },
                        message: '',
                        messageType: '',
                    };
                    this.$refs.historyManager?.focusHistoricalCreateError('form');
                    return;
                }

                const errors = response?.status === 422 ? this.mapReadingValidationErrors(error, payload.formats) : emptyReadingErrors();
                if (response?.status !== 422) errors.form = response?.data?.message || 'Не вдалося додати минуле прочитання.';
                this.readingMutationState = { savingReadingId: null, errors, message: '', messageType: '' };
                this.$refs.historyManager?.focusHistoricalCreateError(this.firstReadingValidationTarget(errors));
            }
        },
        async saveReadingInstance(intent) {
            const statusId = this.actionModal?.id || null;
            const historyGeneration = this.historyLoadGeneration;
            const historyPayload = this.historyState.data;
            const reading = this.readingFromHistory(historyPayload, intent?.readingId);
            if (!statusId || !reading || this.drawerBusy || this.ratingClearConfirmationPending) return;

            const payload = {
                expected_reading_status_lock_version: intent.expectedReadingStatusLockVersion,
                expected_reading_instance_lock_version: intent.expectedReadingInstanceLockVersion,
            };
            if (Object.prototype.hasOwnProperty.call(intent, 'languageCodes')) {
                payload.language_codes = intent.languageCodes;
                payload.primary_language_code = intent.primaryLanguageCode;
            }
            if (Object.prototype.hasOwnProperty.call(intent, 'started')) {
                payload.started = intent.started;
                payload.finished = intent.finished;
            }
            if (Object.prototype.hasOwnProperty.call(intent, 'formats')) payload.formats = intent.formats;
            if (Object.prototype.hasOwnProperty.call(intent, 'rating')) {
                if (intent.rating === null || (Number.isInteger(intent.rating) && intent.rating >= 1 && intent.rating <= 10)) {
                    payload.rating = intent.rating;
                } else {
                    const errors = emptyReadingErrors();
                    errors.rating = 'Оберіть оцінку від 1 до 10.';
                    this.readingMutationState = {
                        savingReadingId: null,
                        errors,
                        message: '',
                        messageType: '',
                    };
                    this.$refs.historyManager?.focusReadingValidation('rating');
                    return;
                }
            }
            if (
                !Object.prototype.hasOwnProperty.call(payload, 'language_codes') &&
                !Object.prototype.hasOwnProperty.call(payload, 'started') &&
                !Object.prototype.hasOwnProperty.call(payload, 'formats') &&
                !Object.prototype.hasOwnProperty.call(payload, 'rating')
            )
                return;

            if (this.requiresCanonicalRatingClearConfirmation(intent, reading)) {
                const confirmed = await this.confirmCanonicalRatingClear();
                if (!confirmed) return;
            }

            const mutationAuthority = this.claimHistoryAuthority(statusId);
            this.readingMutationState = {
                savingReadingId: reading.id,
                errors: emptyReadingErrors(),
                message: '',
                messageType: '',
            };

            try {
                const response = await axios.patch(`/api/account/reading-status/${statusId}/history/${reading.id}`, payload);
                const replaced = this.replaceHistoryPayload(statusId, response.data, mutationAuthority, historyGeneration);
                if (replaced === null) {
                    if (this.readingMutationState.savingReadingId === reading.id) {
                        this.readingMutationState = { ...this.readingMutationState, savingReadingId: null };
                    }
                    return;
                }
                if (!replaced) {
                    throw new Error('Canonical owner history is missing from the server response.');
                }
                this.readingMutationState = {
                    savingReadingId: null,
                    errors: emptyReadingErrors(),
                    message: response.data?.message || 'Дані прочитання оновлено.',
                    messageType: 'success',
                };
                this.$refs.historyManager?.completeReadingSave(reading.id);
                await this.refreshBooks({ ownerFailureMessage: committedMutationRefreshError });
            } catch (error) {
                const response = error?.response;
                if (response?.status === 409) {
                    const conflictPayload = response.data || {};
                    const replaced = this.replaceHistoryPayload(statusId, conflictPayload, mutationAuthority, historyGeneration);
                    if (replaced === null) {
                        if (this.readingMutationState.savingReadingId === reading.id) {
                            this.readingMutationState = { ...this.readingMutationState, savingReadingId: null };
                        }
                        return;
                    }
                    if (!replaced) {
                        this.invalidateHistory(statusId);
                        await this.loadHistory(statusId, { force: true });
                    }
                    this.readingMutationState = {
                        savingReadingId: null,
                        errors: emptyReadingErrors(),
                        message: conflictPayload.message || 'Дані прочитання змінилися. Оновіть їх і повторіть дію.',
                        messageType: 'error',
                    };
                    this.$refs.historyManager?.closeReadingEditorAfterConflict(reading.id);
                    await this.refreshBooks();
                    return;
                }

                const errors =
                    response?.status === 422
                        ? this.mapReadingValidationErrors(error, Array.isArray(payload.formats) ? payload.formats : [])
                        : emptyReadingErrors();
                if (response?.status !== 422) errors.form = response?.data?.message || 'Не вдалося зберегти дані прочитання.';
                this.readingMutationState = {
                    savingReadingId: null,
                    errors,
                    message: '',
                    messageType: '',
                };
                this.$refs.historyManager?.focusReadingValidation(this.firstReadingValidationTarget(errors));
            }
        },
        requiresCanonicalRatingClearConfirmation(intent, reading) {
            return (
                Object.prototype.hasOwnProperty.call(intent || {}, 'rating') &&
                intent.rating === null &&
                reading?.rating !== null &&
                reading?.rating !== undefined &&
                reading?.backs_canonical_rating === true
            );
        },
        async confirmCanonicalRatingClear() {
            const modal = this.$refs.ratingClearDecision;
            if (!modal?.choose) return false;

            const editor = this.$refs.historyManager?.activeReadingEditorRef?.();
            modal.setReturnFocusTarget?.(editor?.$refs?.saveButton || null);
            this.ratingClearConfirmationPending = true;
            try {
                const decision = await modal.choose(
                    'Після збереження оцінка книжки може зникнути або змінитися на оцінку з іншого прочитання.',
                    [{ value: 'remove-rating', label: 'Прибрати оцінку', variant: 'danger' }],
                    'Прибрати оцінку?',
                    'warning',
                    'Скасувати',
                );
                return decision === 'remove-rating';
            } catch {
                return false;
            } finally {
                this.ratingClearConfirmationPending = false;
            }
        },
        async loadHistory(statusId, { force = false } = {}) {
            if (!this.isOwner || !statusId) return false;
            const generation = this.historyLoadGeneration;
            const cached = this.historyCache[statusId];
            if (!force && cached) {
                if (this.isCurrentHistoryLoad(generation, statusId)) {
                    this.historyState = { status: 'loaded', data: cached, error: '' };
                    this.applyHistoryPayload(cached);
                }
                return true;
            }

            if (this.isCurrentHistoryLoad(generation, statusId)) {
                this.historyState = { status: 'loading', data: null, error: '' };
            }

            let request = !force ? this.historyPending[statusId] : null;
            let authority = request ? this.historyPendingAuthority[statusId] : null;
            if (request && !this.ownsHistoryAuthority(statusId, authority)) {
                request = null;
                authority = null;
            }
            try {
                if (!request) {
                    authority = this.claimHistoryAuthority(statusId);
                    request = axios.get(`/api/account/reading-status/${statusId}/history`).then((response) => response.data);
                    this.historyPending[statusId] = request;
                    this.historyPendingAuthority[statusId] = authority;
                }
                const payload = await request;
                if (!this.ownsHistoryAuthority(statusId, authority)) return true;
                this.historyCache[statusId] = payload;
                if (!this.isCurrentHistoryLoad(generation, statusId)) return true;
                this.historyState = { status: 'loaded', data: payload, error: '' };
                this.applyHistoryPayload(payload);
                return true;
            } catch (error) {
                if (!this.ownsHistoryAuthority(statusId, authority)) return false;
                if (!this.isCurrentHistoryLoad(generation, statusId)) return false;
                this.historyState = {
                    status: 'error',
                    data: null,
                    error: error?.response?.data?.message || 'Не вдалося завантажити історію читання.',
                };
                return false;
            } finally {
                if (request && this.historyPending[statusId] === request) {
                    delete this.historyPending[statusId];
                    delete this.historyPendingAuthority[statusId];
                }
            }
        },
        retryHistory() {
            if (this.actionModal?.id) this.loadHistory(this.actionModal.id, { force: true });
        },
        invalidateHistory(statusId) {
            if (!statusId) return;
            this.claimHistoryAuthority(statusId);
            delete this.historyCache[statusId];
        },
        syncBookStatusCache(bookId, statusData = null) {
            if (!bookId) return;
            if (statusData && statusData.status) {
                bookStatusCache.set(bookId, statusData);
            } else {
                bookStatusCache.remove(bookId);
            }
            if (window.bookStatusCache && window.bookStatusCache !== bookStatusCache) {
                if (statusData && statusData.status) {
                    window.bookStatusCache.set(bookId, statusData);
                } else {
                    window.bookStatusCache.remove(bookId);
                }
            }
        },
        focusAfterReadingRecovery(statusId) {
            this.$nextTick(() => {
                const bookAction = this.$refs[`bookAction-${statusId}`];
                const target = Array.isArray(bookAction) ? bookAction[0] : bookAction;
                const fallback = this.$refs.libraryFocusTarget;
                (target?.isConnected ? target : fallback)?.focus();
            });
        },
        async refreshVisibleHistory(statusId, { ownerFailureMessage = '' } = {}) {
            this.invalidateHistory(statusId);
            await this.refreshBooks({ ownerFailureMessage });
            const loaded = await this.loadHistory(statusId, { force: true });
            this.$refs.historyManager?.focusHistory();
            return loaded;
        },
        async manageReading(reading, event = null) {
            if (!reading?.id || this.saving) return;
            const trigger = event?.currentTarget || null;
            const statusId = this.actionModal?.id || null;
            this.saving = true;
            try {
                const snapshot = await fetchReadingRecovery(reading.id, 'history_item_deletion');
                const result = await requestReadingRecovery(snapshot, 'history_item_deletion', { trigger });
                if (result.stale) {
                    alert('Прочитання змінилося. Оновлюємо дані перед повторною дією.');
                    await this.refreshVisibleHistory(statusId);
                    return;
                }
                if (result.completed) {
                    await this.refreshVisibleHistory(statusId, { ownerFailureMessage: committedMutationRefreshError });
                }
            } catch (e) {
                alert(e?.response?.data?.message || 'Не вдалося змінити прочитання.');
            } finally {
                this.saving = false;
            }
        },
        async removeFromLibrary() {
            if (!this.actionModal?.id) return;
            const statusId = this.actionModal.id;
            const bookId = this.actionModal?.book?.id || this.actionModal?.book_id || null;

            this.saving = true;
            try {
                const result = await requestLibraryRemoval((payload) => axios.delete(`/api/account/reading-status/${statusId}`, { data: payload }), {
                    lockVersion: this.editForm.lock_version,
                    openReadingInstanceId: this.actionModal.openReadingInstanceId,
                    openReadingState: this.actionModal.openReadingState,
                });
                if (result.stale) {
                    this.invalidateHistory(statusId);
                    alert('Статус читання змінився. Оновлюємо дані перед повторною дією.');
                    this.closeActionModal();
                    await this.refreshBooks();
                    this.focusAfterReadingRecovery(statusId);
                    return;
                }
                if (!result.completed) return;
                if (result.status) {
                    this.syncBookStatusCache(bookId, {
                        status: result.status,
                        times_read: this.editForm.times_read ?? 0,
                        reading_language: this.historyState.data?.reading_status?.reading_language ?? null,
                    });
                } else {
                    this.syncBookStatusCache(bookId, null);
                }
                this.invalidateHistory(statusId);
                this.closeActionModal();
                await this.refreshBooks({ ownerFailureMessage: committedMutationRefreshError });
                this.focusAfterReadingRecovery(statusId);
            } catch (e) {
                alert(e?.response?.data?.message || 'Не вдалося видалити статус книги.');
            } finally {
                this.saving = false;
            }
        },
        async saveEdit() {
            if (!this.actionModal?.id || this.membershipConflict || this.editForm.status === null) return;
            const statusId = this.actionModal.id;
            const bookId = this.actionModal?.book?.id || this.actionModal?.book_id || null;
            const bookSlug = this.actionModal.bookSlug || this.actionModal?.book?.slug || '';
            const membershipGeneration = this.membershipLoadGeneration;

            this.saving = true;
            try {
                const payload = {
                    status: this.editForm.status,
                    lock_version: this.editForm.lock_version,
                };
                if (this.membershipsLoaded) {
                    payload.library_ids = this.editForm.library_ids || [];
                    payload.membership_snapshot = this.membershipSnapshot;
                }
                const response = await axios.put(`/api/account/reading-status/${this.actionModal.id}`, payload);
                const canonicalStatus = response.data?.status;
                if (!canonicalStatus || typeof canonicalStatus !== 'object' || !Object.prototype.hasOwnProperty.call(canonicalStatus, 'status')) {
                    throw new Error('Canonical reading status is missing from the server response.');
                }
                this.syncBookStatusCache(
                    bookId,
                    canonicalStatus.status === null
                        ? null
                        : {
                              status: canonicalStatus.status,
                              times_read: canonicalStatus.times_read ?? 0,
                              reading_language: canonicalStatus.reading_language ?? null,
                              rating: canonicalStatus.rating ?? null,
                              review: canonicalStatus.review ?? null,
                              started_at: canonicalStatus.started_at ?? null,
                              finished_at: canonicalStatus.finished_at ?? null,
                          },
                );
                this.invalidateHistory(statusId);
                this.closeActionModal();
                await this.refreshBooks({ ownerFailureMessage: committedMutationRefreshError });
                await this.loadHistory(statusId, { force: true });
                this.focusAfterReadingRecovery(statusId);
            } catch (e) {
                if (e?.response?.status === 409 && e?.response?.data?.code === 'STALE_COLLECTION_MEMBERSHIPS') {
                    if (this.isCurrentMembershipLoad(membershipGeneration, statusId, bookSlug)) {
                        this.membershipSnapshot = null;
                        this.membershipsLoaded = false;
                        this.membershipConflict = {
                            message: e.response.data.message || 'Добірки цієї книги змінилися в іншому вікні. Оновіть список і повторіть збереження.',
                            current: e.response.data.current || null,
                        };
                        this.membershipRecoveryError = '';
                    }
                    return;
                }
                alert(e?.response?.data?.message || 'Не вдалося зберегти зміни книги.');
            } finally {
                this.saving = false;
            }
        },
        showUnavailable() {
            alert('Для цього елемента ще немає окремої сторінки редагування.');
        },
    },
};
</script>

<style scoped>
.library-public-toolbar .library-sort-criterion {
    padding-right: 2.5rem;
}

@media (min-width: 768px) {
    .library-public-toolbar .library-sort-criterion {
        width: 13rem;
    }
}

.library-grid-message {
    grid-column: 1 / -1;
}

@media (min-width: 1024px) {
    .library-book-grid {
        grid-template-columns: repeat(auto-fill, minmax(12.75rem, 1fr));
    }
}

.library-shelf-button {
    display: inline-flex;
    min-height: 2.5rem;
    align-items: center;
    gap: 0.5rem;
    border: 1px solid rgb(var(--foxy-border) / 0.7);
    border-radius: 9999px;
    padding: 0.5rem 0.8rem;
    color: rgb(var(--foxy-text-secondary));
    font-size: 0.75rem;
    font-weight: 700;
    white-space: nowrap;
}

.library-shelf-button:hover,
.library-shelf-button--active {
    border-color: rgb(var(--foxy-action) / 0.7);
    color: rgb(var(--foxy-text));
}

.library-shelf-button--active {
    background: rgb(var(--foxy-action) / 0.12);
}

.library-shelf-count {
    min-width: 1.35rem;
    border-radius: 9999px;
    background: rgb(var(--foxy-elevated) / 0.8);
    padding: 0.1rem 0.35rem;
    text-align: center;
    font-variant-numeric: tabular-nums;
}

.book-card-status-badge,
.book-card-rating-chip {
    flex-shrink: 0;
    white-space: nowrap;
}

.book-card-status-badge {
    gap: 0.3rem;
    padding: 0.25rem 0.5rem;
}

.book-preview-trigger {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0 !important;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
}

.book-preview-trigger:focus-visible {
    position: static;
    width: auto;
    height: 2.5rem;
    margin: 0;
    padding: 0.5rem 0.75rem !important;
    overflow: visible;
    clip: auto;
    white-space: normal;
    border-width: 1px;
}
</style>
